import { Button, Spinner } from "flowbite-react";
import { useCallback, useEffect, useRef, useState } from "react";
import Spreadsheet from "react-spreadsheet";
import * as XLSX from "xlsx";
import { UpdateData } from "../../services/firebaseActions/uploaderActions";
import { GetFileExtension } from "../../utilities/utilityService";

const ExcelEditor = ({
  fileName,
  filePath,
  handleClose,
  fileData,
  subCategory,
  handleApi,
}) => {
  const [data, setData] = useState([[]]);
  const [isLoading, setIsLoading] = useState(false);
  const [res, setRes] = useState({ msg: "", type: "" });
  const [userId] = useState(localStorage.userEmail);
  const isUpdatingRef = useRef(false);

  const fileUrl = filePath;

  const handleChange = useCallback((newData) => {
    if (isUpdatingRef.current) return;

    isUpdatingRef.current = true;

    setData((prev) => {
      if (prev === newData) return prev;
      return newData;
    });

    requestAnimationFrame(() => {
      isUpdatingRef.current = false;
    });
  }, []);

  // Load Excel file from URL
  const loadExcel = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(fileUrl);
      const blob = await res.blob();
      const arrayBuffer = await blob.arrayBuffer();

      // Parse Excel
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const sheetData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      // Convert to react-spreadsheet format
      const formatted = sheetData.map((row) =>
        row.map((cell) => ({ value: cell })),
      );

      setData(formatted);
      setIsLoading(false);
    } catch (err) {
      console.error("Error loading Excel:", err);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadExcel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath]);

  // Export back to Excel
  const exportExcel = () => {
    const sheetData = data.map((row) =>
      row.map((cell) => (cell && "value" in cell ? cell.value : "")),
    );
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, fileName);
  };

  const updateFile = () => {
    const sheetData = data.map((row) =>
      row.map((cell) => (cell && "value" in cell ? cell.value : "")),
    );
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const fileType = GetFileExtension(fileName);

    const excelBuffer = XLSX.write(wb, { bookType: fileType, type: "array" });
    const excelBlob = new Blob([excelBuffer], {
      type:
        fileType === "csv"
          ? "text/csv"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    UpdateData(fileData, subCategory, userId, excelBlob, setIsLoading, setRes);
  };

  const handleModal = () => {
    handleClose();
    if (res?.msg !== "") {
      handleApi();
    }
    setRes({ msg: "", type: "" });
  };

  return (
    <>
      {!isLoading && <h2 className="text-lg font-semibold">{fileName}</h2>}
      <span
        className={`text-sm ${
          res?.type === "error" ? "text-red-500" : "text-green-500"
        }`}
      >
        {res?.msg !== "" && res.msg}
      </span>
      {isLoading ? (
        <p>
          <Spinner />
        </p>
      ) : (
        <>
          <div style={{ marginTop: 20 }}>
            <Spreadsheet data={data} onChange={handleChange} />
          </div>
          <div className="mt-4 flex justify-between items-center">
            <button
              className="text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
              onClick={handleModal}
            >
              Cancel
            </button>

            <div className="flex gap-3">
              <Button
                color="gray"
                onClick={exportExcel}
                disabled={!data.length}
              >
                Download file
              </Button>
              <Button
                color="success"
                onClick={updateFile}
                disabled={!data.length}
              >
                Update file
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ExcelEditor;
